import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { assignMeetingToProject, deleteMeetingPermanently, searchMeetings } from "../lib/api";
import type { SearchResult } from "../lib/api";
import { ConfirmPermanentDelete } from "../components/ConfirmPermanentDelete";
import { Toast } from "../components/Toast";
import { promotionToastText } from "../lib/promotionToast";
import { logUserEvent } from "../lib/events";
import { getDefaultWorkspace } from "../lib/workspace";
import { loadActiveProjects, ProjectOption } from "../lib/projects";
import { formatMeetingDate } from "../lib/timestamps";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { StatusChip } from "../ui/StatusChip";
import { Skeleton } from "../ui/Skeleton";
import { EmptyState } from "../ui/EmptyState";
import { LockBadge } from "../ui/LockBadge";
import { Icon } from "../ui/icons";
import { t, tVars } from "../i18n";

interface MeetingRow {
  id: string;
  title: string | null;
  status: string;
  audio_duration_ms: number | null;
  created_at: string;
  recorded_at: string | null;
  locked_at: string | null;
  workspace_id: string;
  project_id: string | null;
  deleted_at: string | null;
  preview_text: string | null;
}

type SortOption =
  | "created_desc"
  | "created_asc"
  | "title_asc"
  | "title_desc"
  | "duration_desc"
  | "duration_asc"
  | "locked_first"
  | "unlocked_first";

type LockFilter = "all" | "locked" | "unlocked";
type ProjectFilter = "all" | "unassigned" | string;

/** Columns the list needs — shared by the main load and the search-extras fetch. */
const MEETING_COLUMNS =
  "id, title, status, audio_duration_ms, created_at, recorded_at, locked_at, workspace_id, project_id, deleted_at, preview_text";

const SORT_LABELS: Record<SortOption, string> = {
  created_desc:   t("library.sortCreatedDesc"),
  created_asc:    t("library.sortCreatedAsc"),
  title_asc:      t("library.sortTitleAsc"),
  title_desc:     t("library.sortTitleDesc"),
  duration_desc:  t("library.sortDurationDesc"),
  duration_asc:   t("library.sortDurationAsc"),
  locked_first:   t("library.sortLockedFirst"),
  unlocked_first: t("library.sortUnlockedFirst"),
};

/** Highlight query term within a snippet string by wrapping with <mark>. */
function highlightSnippet(snippet: string, q: string): React.ReactNode {
  if (!q || !snippet) return snippet;
  const idx = snippet.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return snippet;
  return (
    <>
      {snippet.slice(0, idx)}
      <mark className="search-snippet-mark">
        {snippet.slice(idx, idx + q.length)}
      </mark>
      {snippet.slice(idx + q.length)}
    </>
  );
}

export function MeetingsList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<MeetingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOption, setSortOption] = useState<SortOption>("created_desc");
  const [lockFilter, setLockFilter] = useState<LockFilter>("all");
  // Initialize project filter from URL param on mount; "unassigned" or a project id.
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>(() => {
    const p = searchParams.get("project");
    if (p === "unassigned" || (p && p.length > 0)) return p as ProjectFilter;
    return "all";
  });
  const [showDeleted, setShowDeleted] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [reassigningId, setReassigningId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<MeetingRow | null>(null);
  const [purging, setPurging] = useState(false);
  // Content-search results from /api/search (keyed by meeting_id).
  const [searchResults, setSearchResults] = useState<Map<string, SearchResult>>(new Map());
  // Full meeting rows for search hits — fetched separately because a
  // content-matched meeting may be excluded by the local title ilike filter
  // (its title doesn't contain the query) and so missing from `rows`.
  const [searchHitRows, setSearchHitRows] = useState<MeetingRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enterPressedRef = useRef(false);

  // Load projects list once at mount.
  useEffect(() => {
    let cancelled = false;
    getDefaultWorkspace()
      .then((ws) => loadActiveProjects(ws.id))
      .then((p) => { if (!cancelled) setProjects(p); })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, []);

  const projectsById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, p.name);
    return m;
  }, [projects]);

  // Debounce search input 300ms.
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [search]);

  // Keep the ?project= URL param in sync with the filter selection (replaceState
  // so back-button history is not polluted on every filter change).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (projectFilter === "all") {
          next.delete("project");
        } else {
          next.set("project", projectFilter);
        }
        return next;
      },
      { replace: true },
    );
  }, [projectFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // …and the reverse: when the param changes while the page is already
  // mounted (the sidebar's "Unassigned" link navigates to /?project=…
  // without remounting this component), adopt it into state. The setter
  // guard makes the two sync effects converge instead of looping.
  const urlProject = searchParams.get("project");
  useEffect(() => {
    const desired: ProjectFilter =
      urlProject && urlProject.length > 0 ? (urlProject as ProjectFilter) : "all";
    setProjectFilter((cur) => (cur === desired ? cur : desired));
  }, [urlProject]);

  // Cancel any in-progress rename when query params change.
  useEffect(() => {
    setEditingId(null);
    setEditingTitle("");
  }, [debouncedSearch, sortOption, lockFilter, projectFilter, showDeleted]);

  // Main data load + realtime subscription.
  useEffect(() => {
    let cancelled = false;

    async function load() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let q: any = supabase
        .from("meetings")
        .select(MEETING_COLUMNS);

      if (showDeleted) {
        q = q.not("deleted_at", "is", null);
      } else {
        q = q.is("deleted_at", null);
      }

      if (debouncedSearch.trim()) {
        q = q.ilike("title", `%${debouncedSearch.trim()}%`);
      }

      if (lockFilter === "locked") {
        q = q.not("locked_at", "is", null);
      } else if (lockFilter === "unlocked") {
        q = q.is("locked_at", null);
      }

      if (projectFilter === "unassigned") {
        q = q.is("project_id", null);
      } else if (projectFilter !== "all") {
        q = q.eq("project_id", projectFilter);
      }

      switch (sortOption) {
        case "created_desc":  q = q.order("created_at", { ascending: false }); break;
        case "created_asc":   q = q.order("created_at", { ascending: true }); break;
        case "title_asc":     q = q.order("title", { ascending: true, nullsFirst: false }); break;
        case "title_desc":    q = q.order("title", { ascending: false, nullsFirst: false }); break;
        case "duration_desc": q = q.order("audio_duration_ms", { ascending: false, nullsFirst: false }); break;
        case "duration_asc":  q = q.order("audio_duration_ms", { ascending: true, nullsFirst: true }); break;
        case "locked_first":  q = q.order("locked_at", { ascending: false, nullsFirst: false }); break;
        case "unlocked_first":q = q.order("locked_at", { ascending: true, nullsFirst: true }); break;
      }

      q = q.limit(200);

      const { data, error: qErr } = await q;
      if (cancelled) return;
      if (qErr) setError(qErr.message);
      else setRows(data as MeetingRow[]);
    }

    load();

    // Trailing debounce: the subscription has no row filter, so while a batch
    // is processing every per-phase status write lands here — several per
    // second, each previously firing a full 200-row refetch. Coalesce bursts
    // into one refetch; a lone event still refetches within the window.
    let realtimeDebounce: ReturnType<typeof setTimeout> | null = null;
    const channel = supabase
      .channel("transcripts-list")
      .on("postgres_changes", { event: "*", schema: "public", table: "meetings" }, () => {
        if (cancelled) return;
        if (realtimeDebounce) clearTimeout(realtimeDebounce);
        realtimeDebounce = setTimeout(() => {
          if (!cancelled) load();
        }, 400);
      })
      .subscribe();

    return () => {
      cancelled = true;
      if (realtimeDebounce) clearTimeout(realtimeDebounce);
      supabase.removeChannel(channel);
    };
  }, [debouncedSearch, sortOption, lockFilter, projectFilter, showDeleted]);

  // Content search via /api/search — fires when debouncedSearch >= 2 chars.
  // Also fetches the full meeting rows for hit ids NOT already in `rows`:
  // content-matched meetings whose TITLE doesn't contain the query are excluded
  // by the local title ilike filter, so they're missing from `rows` and must
  // be fetched here. Guard: skip the query when all hit ids are already present
  // (avoids a redundant round-trip on Realtime updates when hits don't change).
  useEffect(() => {
    let cancelled = false;
    if (!debouncedSearch.trim() || debouncedSearch.trim().length < 2) {
      setSearchResults(new Map());
      setSearchHitRows([]);
      return;
    }
    setSearchLoading(true);
    searchMeetings(debouncedSearch.trim())
      .then(async (resp) => {
        if (cancelled) return;
        const map = new Map<string, SearchResult>();
        for (const r of resp.results) map.set(r.meeting_id, r);
        setSearchResults(map);

        const allIds = resp.results.map((r) => r.meeting_id);
        if (allIds.length === 0) {
          setSearchHitRows([]);
          return;
        }
        // Only fetch rows that are NOT already in the realtime `rows` snapshot.
        const existingIds = new Set((rows ?? []).map((r) => r.id));
        const missingIds = allIds.filter((id) => !existingIds.has(id));
        if (missingIds.length === 0) {
          // All hits are already in `rows`; no extra fetch needed.
          setSearchHitRows([]);
          return;
        }
        const { data } = await supabase
          .from("meetings")
          .select(MEETING_COLUMNS)
          .in("id", missingIds)
          .is("deleted_at", null)
          .limit(missingIds.length);
        if (!cancelled) setSearchHitRows((data ?? []) as MeetingRow[]);
      })
      .catch(() => {
        if (!cancelled) {
          setSearchResults(new Map());
          setSearchHitRows([]);
        }
      })
      .finally(() => { if (!cancelled) setSearchLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Apply the active project/lock/archived filters to a row (same predicates
   *  as the Supabase query). Used to gate search-hit rows that bypass the DB
   *  ilike filter so content-search results respect the sidebar filters. */
  function passesFilters(r: MeetingRow): boolean {
    // Archived / deleted filter
    if (showDeleted) {
      if (!r.deleted_at) return false;
    } else {
      if (r.deleted_at) return false;
    }
    // Lock filter
    if (lockFilter === "locked" && !r.locked_at) return false;
    if (lockFilter === "unlocked" && r.locked_at) return false;
    // Project filter
    if (projectFilter === "unassigned" && r.project_id !== null) return false;
    if (projectFilter !== "all" && projectFilter !== "unassigned" && r.project_id !== projectFilter) return false;
    return true;
  }

  // Merge: search hits first (in API order), then the realtime list rows
  // that didn't match the search. A search hit's full row comes from the
  // realtime list when present (fresher after optimistic updates), else
  // from the dedicated search-hits fetch.
  // Regression fix: apply passesFilters to search-hit rows so that a content-
  // search with an active project filter doesn't show other projects' meetings.
  const displayRows = useMemo<MeetingRow[]>(() => {
    if (!rows) return [];
    if (!searchResults.size) return rows;

    const rowMap = new Map<string, MeetingRow>(rows.map((r) => [r.id, r]));
    const hitRowMap = new Map<string, MeetingRow>(searchHitRows.map((r) => [r.id, r]));

    const searchOrdered: MeetingRow[] = [];
    const searchIds = new Set<string>();

    for (const mid of searchResults.keys()) {
      searchIds.add(mid);
      const row = rowMap.get(mid) ?? hitRowMap.get(mid);
      // Apply the same filters that the Supabase query applies to `rows`.
      if (row && passesFilters(row)) searchOrdered.push(row);
    }

    const remainder = rows.filter((r) => !searchIds.has(r.id));
    return [...searchOrdered, ...remainder];
  }, [rows, searchResults, searchHitRows, lockFilter, projectFilter, showDeleted]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Actions ---

  function startEdit(row: MeetingRow) {
    enterPressedRef.current = false;
    setEditingId(row.id);
    setEditingTitle(row.title ?? "");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingTitle("");
  }

  async function commitEdit(row: MeetingRow) {
    if (enterPressedRef.current) { enterPressedRef.current = false; return; }
    if (editingId !== row.id) return;

    const trimmed = editingTitle.trim();
    const effectiveTitle =
      trimmed || `Untitled transcript ${new Date(row.created_at).toISOString().slice(0, 10)}`;

    setEditingId(null);
    setEditingTitle("");
    if (effectiveTitle === row.title) return;

    setRows((prev) =>
      prev ? prev.map((r) => (r.id === row.id ? { ...r, title: effectiveTitle } : r)) : prev,
    );

    const { error: updateErr } = await supabase
      .from("meetings")
      .update({ title: effectiveTitle })
      .eq("id", row.id);

    if (updateErr) {
      setError(updateErr.message);
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === row.id ? { ...r, title: row.title } : r)) : prev,
      );
      return;
    }

    try {
      await logUserEvent({
        meetingId: row.id,
        workspaceId: row.workspace_id,
        eventType: "title_change",
        payload: { old_title: row.title, new_title: effectiveTitle },
      });
    } catch { /* best-effort */ }
  }

  async function softDelete(row: MeetingRow) {
    setRows((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
    const { error: updateErr } = await supabase
      .from("meetings")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", row.id);
    if (updateErr) {
      setError(updateErr.message);
      setRows((prev) => (prev ? [row, ...prev] : [row]));
      return;
    }
    try {
      await logUserEvent({
        meetingId: row.id,
        workspaceId: row.workspace_id,
        eventType: "transcript_deleted",
        payload: {},
      });
    } catch { /* best-effort */ }
  }

  async function restore(row: MeetingRow) {
    setRows((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
    const { error: updateErr } = await supabase
      .from("meetings")
      .update({ deleted_at: null })
      .eq("id", row.id);
    if (updateErr) {
      setError(updateErr.message);
      setRows((prev) => (prev ? [row, ...prev] : [row]));
      return;
    }
    try {
      await logUserEvent({
        meetingId: row.id,
        workspaceId: row.workspace_id,
        eventType: "transcript_restored",
        payload: {},
      });
    } catch { /* best-effort */ }
  }

  async function confirmPurge() {
    if (!purgeTarget) return;
    const id = purgeTarget.id;
    setPurging(true);
    try {
      const result = await deleteMeetingPermanently(id);
      setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
      setPurgeTarget(null);
      if (result.r2_delete_warning) setToast(result.r2_delete_warning);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPurging(false);
    }
  }

  async function reassignProject(row: MeetingRow, newProjectId: string | null) {
    if (newProjectId === row.project_id) { setReassigningId(null); return; }

    const currentProjectName = row.project_id
      ? (projectsById.get(row.project_id) ?? "this project")
      : null;
    const targetName = newProjectId ? (projectsById.get(newProjectId) ?? "the project") : null;
    const message = newProjectId
      ? `Move this meeting to "${targetName}"?`
      : `Unassign this meeting from "${currentProjectName}"?`;

    if (!window.confirm(message)) { setReassigningId(null); return; }

    setReassigningId(null);
    setRows((prev) =>
      prev ? prev.map((r) => (r.id === row.id ? { ...r, project_id: newProjectId } : r)) : prev,
    );

    try {
      const res = await assignMeetingToProject(row.id, newProjectId);
      if (newProjectId) {
        const projectName = projectsById.get(newProjectId) ?? "the project";
        setToast(
          res.promotion_warning ??
            promotionToastText(res.attendees_promoted, res.terms_promoted, projectName),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === row.id ? { ...r, project_id: row.project_id } : r)) : prev,
      );
    }
  }

  if (error) return <p className="error">{error}</p>;

  const isLoading = rows === null;
  const hasRows = !isLoading && displayRows.length > 0;
  const isEmpty = !isLoading && displayRows.length === 0;
  const isSearching = debouncedSearch.trim().length >= 2;

  // Determine snippet for a row: API snippet (content match) takes precedence;
  // fall back to preview_text from DB; then null.
  function rowSnippet(row: MeetingRow): React.ReactNode | null {
    const sr = searchResults.get(row.id);
    if (sr?.snippet) {
      return highlightSnippet(sr.snippet, debouncedSearch.trim());
    }
    if (row.preview_text) return row.preview_text;
    return null;
  }

  return (
    <>
      {/* ---- Header ---- */}
      <div className="library-header">
        <div className="library-header-left">
          <h2 className="library-heading" style={{ margin: 0 }}>
            {showDeleted ? t("library.headingArchived") : t("library.heading")}
          </h2>
          {!isLoading && !showDeleted && (
            <span className="library-count muted">
              {displayRows.length === 1
                ? t("library.transcriptCountOne")
                : tVars("library.transcriptsCount", { n: displayRows.length })}
            </span>
          )}
        </div>
        {!showDeleted && (
          <Link to="/meetings/new">
            <Button variant="primary" size="sm" icon="plus">
              {t("library.newTranscript")}
            </Button>
          </Link>
        )}
      </div>

      {/* ---- Toolbar: single row ---- */}
      <div className="library-toolbar">
        {!showDeleted && (
          <Input
            type="search"
            leadingIcon="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("library.searchPlaceholder")}
            className="library-search"
            aria-label={t("library.searchPlaceholder")}
          />
        )}
        {!showDeleted && (
          <Select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value as SortOption)}
            aria-label={t("library.sortLabel")}
          >
            {(Object.entries(SORT_LABELS) as [SortOption, string][]).map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </Select>
        )}
        {!showDeleted && (
          <Select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value as ProjectFilter)}
            aria-label={t("library.projectLabel")}
          >
            <option value="all">{t("library.allProjects")}</option>
            <option value="unassigned">{t("library.unassigned")}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        )}
        {!showDeleted && (
          <Select
            value={lockFilter}
            onChange={(e) => setLockFilter(e.target.value as LockFilter)}
            aria-label={t("library.lockLabel")}
          >
            <option value="all">{t("library.allLock")}</option>
            <option value="locked">{t("library.lockedOnly")}</option>
            <option value="unlocked">{t("library.unlockedOnly")}</option>
          </Select>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setShowDeleted((v) => !v); setSearch(""); }}
        >
          {showDeleted ? (
            <><Icon name="chevron-left" size={14} /> {t("library.backToLibrary")}</>
          ) : (
            <><Icon name="archive" size={14} /> {t("library.archived")}</>
          )}
        </Button>
      </div>

      {/* ---- Loading skeletons ---- */}
      {isLoading && (
        <div className="meeting-list" role="list" aria-busy="true">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="meeting-row skeleton-row" role="listitem">
              <Skeleton height={18} width="40%" style={{ marginBottom: 6 }} />
              <Skeleton height={13} width="70%" style={{ marginBottom: 6 }} />
              <Skeleton height={13} width="30%" />
            </div>
          ))}
        </div>
      )}

      {/* ---- Empty states ---- */}
      {isEmpty && (
        <>
          {showDeleted && (
            <EmptyState
              icon="archive"
              title={t("library.noArchived")}
            />
          )}
          {!showDeleted && (isSearching || lockFilter !== "all" || projectFilter !== "all") && (
            <EmptyState
              icon="search"
              title={t("library.noMatches")}
            />
          )}
          {!showDeleted && !isSearching && lockFilter === "all" && projectFilter === "all" && (
            <EmptyState
              icon="file-text"
              title={t("library.emptyTitle")}
              description={t("library.emptyDesc")}
              action={
                <Link to="/meetings/new">
                  <Button variant="primary" size="sm" icon="upload">
                    {t("upload.submitButton")}
                  </Button>
                </Link>
              }
            />
          )}
        </>
      )}

      {/* ---- Meeting rows ---- */}
      {hasRows && (
        <div className="meeting-list" role="list">
          {displayRows.map((r) => {
            const snippet = rowSnippet(r);
            const projectName = r.project_id ? projectsById.get(r.project_id) : null;

            return (
              <div
                key={r.id}
                className="meeting-row"
                role="listitem"
              >
                {/* ---- Title line ---- */}
                <div className="meeting-row-title-line">
                  {editingId === r.id ? (
                    <input
                      autoFocus
                      type="text"
                      dir="auto"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          enterPressedRef.current = true;
                          commitEdit(r);
                        }
                        if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                      }}
                      onBlur={() => commitEdit(r)}
                      className="meeting-row-title-input"
                    />
                  ) : (
                    <span className="meeting-row-title-wrap">
                      {r.locked_at && <LockBadge />}
                      {showDeleted ? (
                        <span className="muted" dir="auto">{r.title ?? t("library.untitled")}</span>
                      ) : (
                        <Link
                          to={`/meetings/${r.id}`}
                          className="meeting-row-title-link"
                          dir="auto"
                        >
                          {r.title ?? t("library.untitled")}
                        </Link>
                      )}
                      {!showDeleted && (
                        <button
                          type="button"
                          className="meeting-row-icon-action"
                          onClick={(e) => { e.stopPropagation(); startEdit(r); }}
                          title={t("library.rename")}
                          aria-label={t("library.rename")}
                        >
                          <Icon name="edit" size={13} />
                        </button>
                      )}
                    </span>
                  )}
                </div>

                {/* ---- Snippet / preview line ---- */}
                {snippet && (
                  <div className="meeting-row-snippet muted" dir="auto">
                    {snippet}
                  </div>
                )}

                {/* ---- Meta line: items separated by a muted "·" ---- */}
                <div className="meeting-row-meta">
                  {(() => {
                    const metaItems: React.ReactNode[] = [];

                    // 1. Project chip (or inline reassign select).
                    metaItems.push(
                      reassigningId === r.id && !showDeleted ? (
                        <select
                          autoFocus
                          value={r.project_id ?? ""}
                          onChange={(e) =>
                            reassignProject(r, e.target.value === "" ? null : e.target.value)
                          }
                          onBlur={() => setReassigningId(null)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") { e.preventDefault(); setReassigningId(null); }
                          }}
                          className="meeting-row-project-select"
                        >
                          <option value="">{t("library.unassigned")}</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="meeting-row-meta-item meeting-row-project-chip">
                          {projectName ? (
                            <span className="chip chip-project">
                              <span dir="auto">{projectName}</span>
                            </span>
                          ) : (
                            <span className="chip chip-project chip-project--unassigned">—</span>
                          )}
                          {!showDeleted && (
                            <button
                              type="button"
                              className="meeting-row-icon-action"
                              onClick={(e) => { e.stopPropagation(); setReassigningId(r.id); }}
                              title={t("library.reassignProject")}
                              aria-label={t("library.reassignProject")}
                            >
                              <Icon name="edit" size={13} />
                            </button>
                          )}
                        </span>
                      ),
                    );

                    // 2. Duration.
                    if (r.audio_duration_ms) {
                      metaItems.push(
                        <span className="meeting-row-meta-item">
                          <Icon name="clock" size={12} />
                          <span>{formatDuration(r.audio_duration_ms)}</span>
                        </span>,
                      );
                    }

                    // 3. Meeting date (or muted Added date).
                    metaItems.push(
                      r.recorded_at ? (
                        <span className="meeting-row-meta-item">
                          {formatMeetingDate(r.recorded_at)}
                        </span>
                      ) : (
                        <span className="meeting-row-meta-item muted">
                          {tVars("library.addedDate", { date: new Date(r.created_at).toLocaleDateString() })}
                        </span>
                      ),
                    );

                    // 4. Status chip.
                    metaItems.push(<StatusChip status={r.status} lockedAt={r.locked_at} />);

                    return metaItems.map((node, i) => (
                      <Fragment key={i}>
                        {i > 0 && (
                          <span className="meeting-row-meta-sep" aria-hidden="true">
                            ·
                          </span>
                        )}
                        {node}
                      </Fragment>
                    ));
                  })()}

                  {/* Hover actions (no separator before — pinned to the end) */}
                  <span className="meeting-row-actions">
                    {showDeleted ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => restore(r)}
                        >
                          {t("library.restore")}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setPurgeTarget(r)}
                        >
                          {t("library.deletePermanently")}
                        </Button>
                      </>
                    ) : (
                      (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon="archive"
                          className="meeting-row-reveal"
                          onClick={() => softDelete(r)}
                          title={t("library.archive")}
                        >
                          {t("library.archive")}
                        </Button>
                      )
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Content-search loading indicator */}
      {searchLoading && (
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          {t("common.loading")}
        </p>
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
      <ConfirmPermanentDelete
        open={purgeTarget !== null}
        itemKind="transcript"
        itemName={purgeTarget?.title ?? ""}
        busy={purging}
        onConfirm={confirmPurge}
        onCancel={() => { if (!purging) setPurgeTarget(null); }}
      />
    </>
  );
}

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
